import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Video, Link2, Plus, X, Search, ExternalLink, Trash2, Loader2, Star, CalendarDays, CheckCircle2, Library, Sparkles } from "lucide-react";
import { EmptyState } from "@/components/EmptyState";
import { normalizeUrl } from "@/lib/normalizeUrl";

const YT_ID_RE = /(?:youtube\.com\/watch\?v=|youtube\.com\/shorts\/|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/;
const ytVideoId = (url) => (url || "").match(YT_ID_RE)?.[1] || null;
// FIX 3 (2026-02): was `mqdefault.jpg` (320x180, visibly soft on desktop/retina). `maxresdefault`
// is the sharpest option YouTube offers but doesn't exist for every upload (404s silently) — so
// this renders it first and falls back to `hqdefault` (480x360, always available) on error,
// cropped to a clean 16:9 box via `aspect-video` + `object-cover` (no layout change otherwise).
export const YtThumb = ({ url, className }) => {
  const id = ytVideoId(url);
  const [quality, setQuality] = useState("maxresdefault");
  if (!id) return <div className={`${className} bg-[#F5F5F4] flex items-center justify-center`}><Video className="h-5 w-5 text-[#A8A29E]" /></div>;
  return (
    <img src={`https://i.ytimg.com/vi/${id}/${quality}.jpg`} alt="" loading="lazy"
      onError={() => setQuality((q) => (q === "maxresdefault" ? "hqdefault" : q))}
      className={`${className} object-cover bg-[#F5F5F4]`} />
  );
};

const KIND_META = {
  existing: { label: "Already in the Master Library, will attach", cls: "bg-sky-500/10 text-sky-700 border-sky-500/30", icon: Library },
  internal_new: { label: "New NJM upload, will add to the Master Library", cls: "bg-[#FFC817]/15 text-[#B45309] border-[#FFC817]/40", icon: Sparkles },
  external: { label: "External source, will add to Reference Links only", cls: "bg-orange-500/10 text-orange-700 border-orange-500/30", icon: Link2 },
};

/** Recommended Videos + External Reference Links — Phase 1 (2026-02). Internal-staff-only
 * (founder/partner/pm/coordinator) curation panel on the Project Detail page. `canWrite`
 * gates attach/detach/paste — everyone with `video_links.read` can still browse. */
export const RecommendedVideos = ({ projectId, projectCategory, canWrite }) => {
  const [data, setData] = useState({ videos: [], external_links: [] });
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/v1/videos/project/${projectId}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ videos: [], external_links: [] }))
      .finally(() => setLoading(false));
  };
  useEffect(load, [projectId]);

  const removeVideo = async (videoId) => {
    setBusyId(videoId);
    try {
      await api.delete(`/v1/videos/project/${projectId}/video/${videoId}`);
      toast.success("Removed from this project's recommended videos");
      load();
    } catch { toast.error("Couldn't remove this video"); }
    finally { setBusyId(null); }
  };

  const removeExternal = async (linkId) => {
    setBusyId(linkId);
    try {
      await api.delete(`/v1/videos/project/${projectId}/external/${linkId}`);
      toast.success("Removed from this project's reference links");
      load();
    } catch { toast.error("Couldn't remove this link"); }
    finally { setBusyId(null); }
  };

  const isEmpty = data.videos.length === 0 && data.external_links.length === 0;

  // Project Workspace Phase 1 (2026-09): "Recommended Videos' empty state is one line of text
  // inside a full bordered Section — collapse the whole card, not just the inner copy." When
  // genuinely empty (post-load), skip the separate header+body wrapper entirely and render ONE
  // compact row — title, empty copy, and the Add action all on the same line.
  if (!loading && isEmpty) {
    return (
      <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF]" data-testid="recommended-videos-section">
        <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="recommended-videos-empty">
          <span className="text-[12.5px] text-[#9BA1A4]">
            <span className="text-xs uppercase tracking-wider text-[#A8A29E] mr-2">Recommended Videos</span>
            No recommended videos yet
          </span>
          {canWrite && (
            <button onClick={() => setPicker(true)} data-testid="recommended-videos-add-btn"
              className="shrink-0 text-[11px] font-bold uppercase tracking-wide text-[#495053] hover:text-[#B45309]">
              + Add Video / Link
            </button>
          )}
        </div>
        {picker && <AddVideoModal projectId={projectId} projectCategory={projectCategory} onClose={() => setPicker(false)} onAdded={() => { load(); }} />}
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF]" data-testid="recommended-videos-section">
      <header className="border-b border-[#E7E5E4] px-4 py-3 flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-[#A8A29E]">Recommended Videos</span>
        {canWrite && (
          <button onClick={() => setPicker(true)} data-testid="recommended-videos-add-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] px-2.5 py-1 text-[10px] uppercase tracking-wider text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309]">
            <Plus className="h-3 w-3" /> Add Video / Link
          </button>
        )}
      </header>
      <div className="px-4 py-4" data-testid="recommended-videos-content">
        {loading ? (
          <div className="text-sm text-[#A8A29E]">Loading…</div>
        ) : (
          <div className="space-y-4">
            {data.videos.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

                {data.videos.map((v) => (
                  <div key={v.id} data-testid={`recommended-video-card-${v.id}`} className="group relative rounded-md border border-[#E7E5E4] overflow-hidden">
                    <YtThumb url={v.video_url} className="w-full aspect-video" />
                    <div className="p-2.5">
                      <a href={normalizeUrl(v.video_url)} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-[#1C1C1E] hover:text-[#B45309] line-clamp-2">{v.name}</a>
                      <div className="mt-1.5 flex items-center gap-2 text-[10px] text-[#A8A29E]">
                        <span className="inline-flex items-center gap-0.5"><Star className="h-3 w-3 text-[#FFC817]" /> {v.recommendation_score ?? 0}</span>
                        {v.date_published && <span className="inline-flex items-center gap-0.5"><CalendarDays className="h-3 w-3" /> {v.date_published}</span>}
                        <span className="truncate">{v.category}</span>
                      </div>
                    </div>
                    {canWrite && (
                      <button onClick={() => removeVideo(v.id)} disabled={busyId === v.id} data-testid={`recommended-video-remove-${v.id}`}
                        className="absolute top-1.5 right-1.5 rounded-full bg-black/60 p-1 text-white opacity-0 group-hover:opacity-100 hover:bg-red-600 transition-opacity disabled:opacity-50">
                        {busyId === v.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {data.external_links.length > 0 && (
              <div className="divide-y divide-[#E7E5E4] rounded-md border border-[#E7E5E4]">
                {data.external_links.map((l) => (
                  <div key={l.id} data-testid={`recommended-external-link-${l.id}`} className="flex items-center justify-between gap-2 px-3 py-2">
                    <a href={normalizeUrl(l.link)} target="_blank" rel="noopener noreferrer" className="min-w-0 inline-flex items-center gap-1.5 text-xs text-[#57534E] hover:text-[#B45309] truncate">
                      <Link2 className="h-3.5 w-3.5 shrink-0 text-[#A8A29E]" /> <span className="truncate">{l.name}</span> <ExternalLink className="h-3 w-3 shrink-0" />
                    </a>
                    {canWrite && (
                      <button onClick={() => removeExternal(l.id)} disabled={busyId === l.id} data-testid={`recommended-external-remove-${l.id}`} className="text-[#A8A29E] hover:text-red-600 shrink-0">
                        {busyId === l.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {picker && <AddVideoModal projectId={projectId} projectCategory={projectCategory} onClose={() => setPicker(false)} onAdded={() => { load(); }} />}
    </section>
  );
};

const TABS = [{ key: "library", label: "Browse Library", icon: Library }, { key: "paste", label: "Paste a Link", icon: Link2 }];

const AddVideoModal = ({ projectId, projectCategory, onClose, onAdded }) => {
  const [tab, setTab] = useState("library");

  // --- Browse Library tab ---
  const [category, setCategory] = useState(projectCategory || "");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [libLoading, setLibLoading] = useState(true);
  const [attachingId, setAttachingId] = useState(null);

  const loadLibrary = () => {
    setLibLoading(true);
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (q) params.set("q", q);
    api.get(`/v1/videos/library?${params.toString()}`).then((r) => setItems(r.data.items || [])).catch(() => setItems([])).finally(() => setLibLoading(false));
  };
  useEffect(() => { const t = setTimeout(loadLibrary, 200); return () => clearTimeout(t); }, [category, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const attachExisting = async (videoId) => {
    setAttachingId(videoId);
    try {
      await api.post(`/v1/videos/project/${projectId}/add-existing`, { video_id: videoId });
      toast.success("Added to Recommended Videos");
      onAdded();
    } catch (e) { toast.error(e?.response?.data?.detail?.code || "Couldn't add this video"); }
    finally { setAttachingId(null); }
  };

  // --- Paste a Link tab ---
  const [url, setUrl] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [titleOverride, setTitleOverride] = useState("");
  const [description, setDescription] = useState("");
  const [confirming, setConfirming] = useState(false);

  const doPreview = async () => {
    if (!url.trim()) return;
    setPreviewing(true);
    setPreview(null);
    try {
      const r = await api.post(`/v1/videos/project/${projectId}/preview-link`, { url: url.trim() });
      setPreview(r.data);
      setTitleOverride(r.data.title || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't read this link");
    } finally { setPreviewing(false); }
  };

  const doConfirm = async () => {
    if (preview?.requires_title && !titleOverride.trim()) {
      toast.error("Add a title before confirming: the source couldn't be auto-detected");
      return;
    }
    setConfirming(true);
    try {
      const r = await api.post(`/v1/videos/project/${projectId}/confirm-link`, {
        url: url.trim(), title: titleOverride.trim() || undefined, description: description.trim() || undefined,
      });
      const successMsg = r.data.type === "existing_library" ? "Attached the existing library video"
        : r.data.type === "internal_created" ? "Added to the Video Links Master Library and attached"
        : "Added to External Reference Links";
      toast.success(successMsg);
      onAdded();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't add this link");
    } finally { setConfirming(false); }
  };

  const kindMeta = preview ? KIND_META[preview.kind] : null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="add-recommended-video-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="add-recommended-video-backdrop" />
      <div className="relative w-full max-w-lg h-full bg-[#FFFFFF] border-l border-[#E7E5E4] overflow-y-auto shadow-2xl animate-in slide-in-from-right duration-200">
        <div className="flex items-center justify-between border-b border-[#E7E5E4] px-5 py-4 sticky top-0 bg-[#FFFFFF] z-10">
          <h2 className="font-editorial text-xl tracking-tight">Add Video / Link</h2>
          <button data-testid="add-recommended-video-close" onClick={onClose} className="text-[#A8A29E] hover:text-[#1C1C1E]"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex border-b border-[#E7E5E4] px-5">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`add-video-tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-xs border-b-2 -mb-px ${tab === t.key ? "border-[#FFC817] text-[#1C1C1E]" : "border-transparent text-[#A8A29E] hover:text-[#57534E]"}`}>
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "library" && (
          <div className="p-5 space-y-3">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-[#A8A29E]" />
                <input data-testid="video-library-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title…"
                  className="w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-[#FFC817]/50" />
              </div>
              <select data-testid="video-library-category-filter" value={category} onChange={(e) => setCategory(e.target.value)}
                className="rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-2 py-2 text-xs text-[#57534E] focus:outline-none focus:border-[#FFC817]/50">
                <option value="">All categories</option>
                {[...new Set(items.map((i) => i.category).filter(Boolean).concat(projectCategory ? [projectCategory] : []))].sort().map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {projectCategory && (
              <div className="text-[10px] text-[#A8A29E]">Sorted by Recommendation Score, then Date Published (newest first). Filtered to <span className="text-[#57534E]">{category || "all categories"}</span> — this project's category is <span className="text-[#57534E]">{projectCategory}</span>.</div>
            )}
            {libLoading ? (
              <div className="text-sm text-[#A8A29E] py-6 text-center">Loading…</div>
            ) : items.length === 0 ? (
              <div className="text-sm text-[#A8A29E] py-6 text-center">No videos match this filter.</div>
            ) : (
              <div className="divide-y divide-[#E7E5E4] max-h-[60vh] overflow-y-auto">
                {items.map((v) => (
                  <div key={v.id} data-testid={`video-library-row-${v.id}`} className="flex items-center gap-3 py-2.5">
                    <YtThumb url={v.video_url} className="w-16 aspect-video rounded shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-[#1C1C1E] truncate flex items-center gap-1.5">
                        {v.name}
                        {v.status === "Needs Review" && (
                          <span data-testid={`video-library-needs-review-${v.id}`} className="shrink-0 rounded-full border border-orange-500/30 bg-orange-500/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-orange-700">Needs Review</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-[#A8A29E]">
                        <span className="inline-flex items-center gap-0.5"><Star className="h-3 w-3 text-[#FFC817]" /> {v.recommendation_score ?? 0}</span>
                        {v.date_published && <span>{v.date_published}</span>}
                        <span className="truncate">{v.category}</span>
                      </div>
                    </div>
                    <button onClick={() => attachExisting(v.id)} disabled={attachingId === v.id} data-testid={`video-library-attach-${v.id}`}
                      className="shrink-0 inline-flex items-center gap-1 rounded-md border border-[#FFC817]/40 bg-[#FFC817]/10 px-2 py-1 text-[10px] text-[#B45309] hover:bg-[#FFC817]/20 disabled:opacity-50">
                      {attachingId === v.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Add
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "paste" && (
          <div className="p-5 space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Video / Link URL</label>
              <div className="flex gap-2 mt-1">
                <input data-testid="paste-link-url-input" value={url} onChange={(e) => { setUrl(e.target.value); setPreview(null); }} placeholder="https://…"
                  className="flex-1 rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm focus:outline-none focus:border-[#FFC817]/50" />
                <button onClick={doPreview} disabled={!url.trim() || previewing} data-testid="paste-link-preview-btn"
                  className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] px-3 py-2 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309] disabled:opacity-50">
                  {previewing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Preview"}
                </button>
              </div>
            </div>

            {preview && (
              <div className="rounded-md border border-[#E7E5E4] p-3 space-y-3" data-testid="paste-link-preview-card">
                <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-wider ${kindMeta.cls}`}>
                  <kindMeta.icon className="h-3 w-3" /> {kindMeta.label}
                </div>
                {preview.thumbnail_url && <img src={preview.thumbnail_url} className="w-full h-32 object-cover rounded" />}
                {preview.kind === "existing" ? (
                  <div className="text-sm text-[#1C1C1E]">{preview.existing_video.name}</div>
                ) : preview.requires_title ? (
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Title (couldn't auto-detect — required)<span className="text-[#B45309]"> *</span></label>
                    <input data-testid="paste-link-title-input" value={titleOverride} onChange={(e) => setTitleOverride(e.target.value)} placeholder="e.g. Client's own promo video"
                      className="mt-1 w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm focus:outline-none focus:border-[#FFC817]/50" />
                  </div>
                ) : (
                  <div className="text-sm text-[#1C1C1E]">{titleOverride}</div>
                )}
                {preview.kind === "external" && (
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Description (optional)</label>
                    <textarea data-testid="paste-link-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
                      className="mt-1 w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm focus:outline-none focus:border-[#FFC817]/50" />
                  </div>
                )}
                <button onClick={doConfirm} disabled={confirming} data-testid="paste-link-confirm-btn"
                  className="w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-[#1C1C1E] px-3 py-2 text-xs text-white hover:bg-[#B45309] disabled:opacity-50">
                  {confirming ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Confirm & Add
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
